import { useMemo } from 'react';
import { usePipelineStore } from '@/store/pipeline.store';
import type { PipelineJob, PipelineStatus } from '@/types/pipeline';
import '@/styles/analytics.css';

interface ActivityItem {
  id: string;
  text: string;
  jobTitle: string;
  company: string;
  type: PipelineStatus;
  timestamp: string; // ISO date
}

/**
 * Truncate a string to a max length, adding ellipsis if needed.
 */
function truncate(str: string, max: number): string {
  if (str.length <= max) return str;
  return str.slice(0, max) + '...';
}

/**
 * Format an ISO date as a human-readable relative time string.
 */
function formatRelativeTime(isoDate: string): string {
  const date = new Date(isoDate);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;

  const diffWeeks = Math.floor(diffDays / 7);
  if (diffWeeks < 5) return `${diffWeeks}w ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Build activity items from pipeline jobs' timestamps.
 */
function buildActivities(jobs: PipelineJob[]): ActivityItem[] {
  const activities: ActivityItem[] = [];

  for (const job of jobs) {
    const title = truncate(job.title, 40);
    const company = truncate(job.company, 30);

    // Every job has addedAt
    activities.push({
      id: `${job.id}-saved`,
      text: `Added <strong>${title}</strong> at ${company}`,
      jobTitle: job.title,
      company: job.company,
      type: 'saved',
      timestamp: job.addedAt,
    });

    // If applied
    if (job.appliedAt) {
      activities.push({
        id: `${job.id}-applied`,
        text: `Applied to <strong>${title}</strong>`,
        jobTitle: job.title,
        company: job.company,
        type: 'applied',
        timestamp: job.appliedAt,
      });
    }

    // If interview
    if (job.interviewAt) {
      activities.push({
        id: `${job.id}-interview`,
        text: `Interview scheduled for <strong>${title}</strong>`,
        jobTitle: job.title,
        company: job.company,
        type: 'interview',
        timestamp: job.interviewAt,
      });
    }

    // If offer
    if (job.status === 'offer') {
      activities.push({
        id: `${job.id}-offer`,
        text: `Offer from <strong>${company}</strong>!`,
        jobTitle: job.title,
        company: job.company,
        type: 'offer',
        timestamp: job.interviewAt || job.appliedAt || job.addedAt,
      });
    }

    // If rejected
    if (job.status === 'rejected') {
      activities.push({
        id: `${job.id}-rejected`,
        text: `Declined <strong>${title}</strong>`,
        jobTitle: job.title,
        company: job.company,
        type: 'rejected',
        timestamp: job.appliedAt || job.addedAt,
      });
    }
  }

  // Sort newest first
  activities.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  return activities.slice(0, 10);
}

function getDotClass(type: PipelineStatus): string {
  switch (type) {
    case 'saved':
      return 'activity-item__dot activity-item__dot--primary';
    case 'applied':
      return 'activity-item__dot activity-item__dot--success';
    case 'interview':
      return 'activity-item__dot activity-item__dot--warning';
    case 'offer':
      return 'activity-item__dot activity-item__dot--success';
    case 'rejected':
      return 'activity-item__dot activity-item__dot--error';
  }
}

export function ActivityFeed() {
  const jobs = usePipelineStore((s) => s.jobs);
  const activities = useMemo(() => buildActivities(jobs), [jobs]);

  return (
    <div className="activity-feed">
      <h2 className="activity-feed__title">Recent Activity</h2>

      {activities.length === 0 ? (
        <div className="activity-feed__empty">
          <svg className="activity-feed__empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M12 8v4l3 3" />
            <circle cx="12" cy="12" r="10" />
          </svg>
          <p className="activity-feed__empty-text">
            No activity yet. Add jobs to your pipeline to start tracking.
          </p>
        </div>
      ) : (
        <ul className="activity-feed__list">
          {activities.map((activity) => (
            <li key={activity.id} className="activity-item">
              <span className={getDotClass(activity.type)} aria-hidden="true" />
              <div className="activity-item__content">
                <p
                  className="activity-item__text"
                  dangerouslySetInnerHTML={{ __html: activity.text }}
                />
                <p className="activity-item__time">
                  {formatRelativeTime(activity.timestamp)}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
